/**
 * Rentabilité de la boutique en ligne (26/10/2026).
 *
 * Marge de chaque commande après marchandise, remboursements, frais de paiement,
 * temps de préparation, emballages et livraison. Les coûts qu'on ne mesure pas se
 * RÈGLENT ici (coût horaire, coût au km, coût d'un colis) ; un coût inconnu est
 * nommé sur la commande, jamais remplacé par zéro en silence.
 */
import { useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';

type Zone = 'caisse' | 'frigo' | 'pain' | 'autre';
interface Reglages {
  cout_horaire_cents: number;
  cout_km_cents: number;
  emballage_cents: Record<Zone, number>;
}
interface Bloc {
  commandes: number;
  ca_ht_cents: number;
  marchandise_cents: number;
  rembourse_ht_cents: number;
  frais_paiement_cents: number;
  preparation_cents: number;
  emballage_cents: number;
  livraison_cents: number;
  frais_livraison_ht_cents: number;
  marge_cents: number;
  marge_par_commande_cents: number | null;
  completes: number;
  preparation_mediane_minutes: number | null;
}
interface Commande {
  id: string;
  numero: string;
  date: string;
  client: string;
  mode: 'retrait' | 'livraison';
  ca_ht_cents: number;
  marchandise_cents: number | null;
  rembourse_ht_cents: number;
  frais_paiement_cents: number | null;
  preparation_secondes: number | null;
  preparation_cents: number | null;
  colis: Zone[];
  emballage_cents: number;
  livraison_cents: number | null;
  marge_cents: number;
  inconnus: string[];
}
interface Rapport {
  reglages: Reglages;
  commandes: Commande[];
  total: Bloc;
  retrait: Bloc;
  livraison: Bloc;
  frais_recharges_folies_cents: number;
}

const eur = (c: number | null | undefined) =>
  c === null || c === undefined ? '—' : `${(c / 100).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const versCents = (v: string) => Math.max(0, Math.round(Number(v.replace(',', '.')) * 100) || 0);
const versEuros = (c: number) => (c ? (c / 100).toFixed(2).replace('.', ',') : '');

const ZONES: Array<{ zone: Zone; libelle: string }> = [
  { zone: 'caisse', libelle: '📦 Caisse' },
  { zone: 'frigo', libelle: '🧊 Frigo' },
  { zone: 'pain', libelle: '🥖 Pain' },
  { zone: 'autre', libelle: '🛍️ Autre' },
];

export default function RentabiliteEshopPage() {
  const { tenant, reloadTenant } = useTenant();
  const [du, setDu] = useState(() => iso(new Date(Date.now() - 29 * 86400_000)));
  const [au, setAu] = useState(() => iso(new Date()));
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [erreur, setErreur] = useState('');
  const [champs, setChamps] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);

  const charger = async () => {
    setErreur('');
    try {
      const r = await api.get<Rapport>(`/analytics/rentabilite-eshop?from=${du}&to=${au}`);
      setRapport(r);
      setChamps({
        horaire: versEuros(r.reglages.cout_horaire_cents),
        km: versEuros(r.reglages.cout_km_cents),
        ...Object.fromEntries(ZONES.map((z) => [z.zone, versEuros(r.reglages.emballage_cents[z.zone])])),
      });
    } catch (e: any) {
      setErreur(e?.response?.data?.message || e?.message || 'Rapport illisible');
    }
  };
  useEffect(() => {
    void charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [du, au]);

  const enregistrer = async () => {
    if (!tenant?.id) return;
    setEnvoi(true);
    try {
      await api.updateTenantConfig(tenant.id, {
        ecommerce: {
          rentabilite: {
            cout_horaire_cents: versCents(champs.horaire || ''),
            cout_km_cents: versCents(champs.km || ''),
            emballage_cents: Object.fromEntries(ZONES.map((z) => [z.zone, versCents(champs[z.zone] || '')])),
          },
        },
      });
      await reloadTenant();
      await charger();
    } catch (e: any) {
      setErreur(e?.response?.data?.message || e?.message || 'Réglages non enregistrés');
    } finally {
      setEnvoi(false);
    }
  };

  const etapes: Array<{ libelle: string; cle: keyof Bloc; signe: 1 | -1 }> = [
    { libelle: 'Chiffre d’affaires HT', cle: 'ca_ht_cents', signe: 1 },
    { libelle: 'Marchandise (prix d’achat)', cle: 'marchandise_cents', signe: -1 },
    { libelle: 'Remboursements (pesée, manquants)', cle: 'rembourse_ht_cents', signe: -1 },
    { libelle: 'Frais de paiement (carte)', cle: 'frais_paiement_cents', signe: -1 },
    { libelle: 'Préparation (temps × coût horaire)', cle: 'preparation_cents', signe: -1 },
    { libelle: 'Emballages (colis)', cle: 'emballage_cents', signe: -1 },
    { libelle: 'Livraison (temps + km)', cle: 'livraison_cents', signe: -1 },
    { libelle: 'Frais de livraison facturés', cle: 'frais_livraison_ht_cents', signe: 1 },
  ];

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rentabilité de la boutique en ligne</h1>
          <p className="mt-2 text-gray-600">Ce qu'il reste de chaque commande après la marchandise, la préparation et la livraison.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={du} onChange={(e) => e.target.value && setDu(e.target.value)} className="min-h-[44px] rounded-lg border border-gray-300 px-3" aria-label="Du" />
          <span className="text-gray-500">→</span>
          <input type="date" value={au} onChange={(e) => e.target.value && setAu(e.target.value)} className="min-h-[44px] rounded-lg border border-gray-300 px-3" aria-label="Au" />
        </div>
      </div>

      {erreur && <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-800">{erreur}</p>}

      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="text-lg font-bold text-gray-900">Coûts à régler</h2>
        <p className="text-sm text-gray-600">Ce que le système ne peut pas mesurer. Tant qu'un réglage est vide, le coût correspondant apparaît comme « inconnu ».</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <ChampEuros libelle="Coût horaire chargé" suffixe="€/h" valeur={champs.horaire || ''} onChange={(v) => setChamps({ ...champs, horaire: v })} />
          <ChampEuros libelle="Camionnette" suffixe="€/km" valeur={champs.km || ''} onChange={(v) => setChamps({ ...champs, km: v })} />
          {ZONES.map((z) => (
            <ChampEuros key={z.zone} libelle={`Colis ${z.libelle}`} suffixe="€" valeur={champs[z.zone] || ''} onChange={(v) => setChamps({ ...champs, [z.zone]: v })} />
          ))}
        </div>
        <button onClick={enregistrer} disabled={envoi} className="min-h-[44px] px-5 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50">
          {envoi ? 'Enregistrement…' : 'Enregistrer les coûts'}
        </button>
      </section>

      {rapport && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Chiffre libelle="Commandes" valeur={String(rapport.total.commandes)} detail={`${rapport.retrait.commandes} retrait · ${rapport.livraison.commandes} livraison`} />
            <Chiffre libelle="Marge après tout" valeur={eur(rapport.total.marge_cents)} detail={rapport.total.ca_ht_cents ? `${Math.round((100 * rapport.total.marge_cents) / rapport.total.ca_ht_cents)} % du CA HT` : ''} />
            <Chiffre libelle="Marge par commande" valeur={eur(rapport.total.marge_par_commande_cents)} detail="moyenne" />
            <Chiffre
              libelle="Préparation (médiane)"
              valeur={rapport.total.preparation_mediane_minutes === null ? '—' : `${rapport.total.preparation_mediane_minutes} min`}
              detail={`${rapport.total.completes}/${rapport.total.commandes} commandes au calcul complet`}
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2 text-right">Toutes</th>
                  <th className="px-3 py-2 text-right">Retrait</th>
                  <th className="px-3 py-2 text-right">Livraison</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 tabular-nums">
                {etapes.map((e) => (
                  <tr key={e.cle}>
                    <td className="px-3 py-2">{e.signe < 0 ? '− ' : '+ '}{e.libelle}</td>
                    {[rapport.total, rapport.retrait, rapport.livraison].map((b, i) => (
                      <td key={i} className="px-3 py-2 text-right">{eur(b[e.cle] as number)}</td>
                    ))}
                  </tr>
                ))}
                <tr className="font-bold bg-gray-50">
                  <td className="px-3 py-2">= Marge</td>
                  {[rapport.total, rapport.retrait, rapport.livraison].map((b, i) => (
                    <td key={i} className="px-3 py-2 text-right">{eur(b.marge_cents)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {rapport.frais_recharges_folies_cents > 0 && (
            <p className="text-sm text-gray-600">
              À part : {eur(rapport.frais_recharges_folies_cents)} de frais Stripe sur les recharges FOLIES de la période (les commandes payées en FOLIES n'en ont pas).
            </p>
          )}

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2">Commande</th>
                  <th className="px-3 py-2 text-right">CA HT</th>
                  <th className="px-3 py-2 text-right">Marchandise</th>
                  <th className="px-3 py-2 text-right">Préparation</th>
                  <th className="px-3 py-2 text-right">Colis</th>
                  <th className="px-3 py-2 text-right">Livraison</th>
                  <th className="px-3 py-2 text-right">Marge</th>
                  <th className="px-3 py-2">Manque</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 tabular-nums">
                {rapport.commandes.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Aucune commande payée sur la période.</td></tr>
                )}
                {[...rapport.commandes].reverse().map((c) => (
                  <tr key={c.id}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="font-medium">{c.numero}</span>
                      <span className="block text-xs text-gray-500">
                        {new Date(c.date).toLocaleDateString('fr-BE')} · {c.client} · {c.mode === 'livraison' ? '🚚 livraison' : 'retrait'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{eur(c.ca_ht_cents)}</td>
                    <td className="px-3 py-2 text-right">{eur(c.marchandise_cents)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {c.preparation_secondes === null ? '—' : `${Math.round(c.preparation_secondes / 60)} min`}
                      {c.preparation_cents !== null && <span className="block text-xs text-gray-500">{eur(c.preparation_cents)}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{c.colis.length || '—'}</td>
                    <td className="px-3 py-2 text-right">{c.mode === 'livraison' ? eur(c.livraison_cents) : ''}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${c.marge_cents < 0 ? 'text-red-700' : 'text-gray-900'}`}>{eur(c.marge_cents)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {c.inconnus.map((i) => (
                          <span key={i} className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">{i}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Chiffre({ libelle, valeur, detail }: { libelle: string; valeur: string; detail?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="text-sm text-gray-600">{libelle}</div>
      <div className="text-2xl font-bold tabular-nums text-gray-900">{valeur}</div>
      {detail && <div className="text-xs text-gray-500 mt-0.5">{detail}</div>}
    </div>
  );
}

function ChampEuros({ libelle, suffixe, valeur, onChange }: { libelle: string; suffixe: string; valeur: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      {libelle}
      <span className="mt-1 flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          value={valeur}
          onChange={(e) => onChange(e.target.value.replace(/[^\d,.]/g, ''))}
          placeholder="0,00"
          className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 tabular-nums"
        />
        <span className="shrink-0 text-gray-500">{suffixe}</span>
      </span>
    </label>
  );
}
